import React, { useMemo } from 'react';
import {
    ComposableMap,
    Geographies,
    Geography,
    Graticule,
    Marker,
} from 'react-simple-maps';

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Sample flight positions scattered across the globe for preview
const SAMPLE_FLIGHTS = [
    { id: 1, coordinates: [-74, 40], rotation: 45 },      // New York area
    { id: 2, coordinates: [-118, 34], rotation: 315 },    // Los Angeles
    { id: 3, coordinates: [-0.1, 51], rotation: 90 },     // London
    { id: 4, coordinates: [2.3, 48.8], rotation: 120 },   // Paris
    { id: 5, coordinates: [139, 35], rotation: 225 },     // Tokyo
    { id: 6, coordinates: [151, -33], rotation: 180 },    // Sydney
    { id: 7, coordinates: [55, 25], rotation: 60 },       // Dubai
    { id: 8, coordinates: [103, 1], rotation: 30 },       // Singapore
    { id: 9, coordinates: [-99, 19], rotation: 270 },     // Mexico City
    { id: 10, coordinates: [116, 40], rotation: 150 },    // Beijing
    { id: 11, coordinates: [77, 28], rotation: 200 },     // Delhi
    { id: 12, coordinates: [-43, -23], rotation: 100 },   // Rio de Janeiro
];

// Mini plane icon for preview
const MiniPlaneIcon = ({ size = 6, rotation = 0, color = "#ffffff" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        style={{
            transform: `rotate(${rotation}deg)`,
            filter: 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))'
        }}
    >
        <path
            d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
            fill={color}
        />
    </svg>
);

/**
 * MapPreview - A simplified, non-interactive preview of the Azimuthal map
 * Used in the Share-to-Save modal to showcase the app's main feature
 */
export const MapPreview = ({ className = "", style = {} }) => {
    const projectionConfig = useMemo(() => ({
        rotate: [0, -90, 0],
        scale: 120,
        center: [0, 0]
    }), []);

    return (
        <div 
            className={`relative overflow-hidden ${className}`}
            style={{ 
                background: '#071D35',
                borderRadius: '12px',
                ...style 
            }}
        >
            <ComposableMap
                projection="geoAzimuthalEquidistant"
                projectionConfig={projectionConfig}
                style={{ width: '100%', height: '100%' }}
            >
                {/* Graticule (grid lines) - subtle */}
                <Graticule 
                    stroke="#334155" 
                    strokeWidth={0.3}
                    step={[30, 30]}
                />

                {/* Countries - simplified styling matching main map */}
                <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                        geographies.map((geo) => (
                            <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                fill="#1e3a2f"
                                stroke="#3d5a4f"
                                strokeWidth={0.3}
                                style={{
                                    default: { outline: 'none' },
                                    hover: { outline: 'none' },
                                    pressed: { outline: 'none' }
                                }}
                            />
                        ))
                    }
                </Geographies>

                {/* Sample Flight Markers */}
                {SAMPLE_FLIGHTS.map((flight) => (
                    <Marker
                        key={flight.id}
                        coordinates={flight.coordinates}
                    >
                        <MiniPlaneIcon
                            size={5}
                            rotation={flight.rotation}
                            color={flight.id % 3 === 0 ? '#fbbf24' : '#ffffff'}
                        />
                    </Marker>
                ))}
            </ComposableMap>

            {/* Subtle gradient overlay at bottom */}
            <div 
                className="absolute inset-x-0 bottom-0 h-12 pointer-events-none"
                style={{
                    background: 'linear-gradient(to top, rgba(7, 29, 53, 0.9), transparent)'
                }}
            />
        </div>
    );
};

export default MapPreview;
